import { ExportService } from './exportService';

// Re-export EditorState shape (mirrors what exportService uses internally)
type EditorBlock = {
  id: string;
  type: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  content: any;
  style: any;
  zIndex: number;
  visible?: boolean;
};

type EditorPage = {
  id: string;
  name: string;
  blocks: EditorBlock[];
  canvasSize: { width: number; height: number };
  background?: any;
};

type EditorState = {
  pages: EditorPage[];
  currentPageId: string;
  selectedBlockId: string | null;
  zoom: number;
  showGrid: boolean;
};

export type ExportFormat = 'pdf' | 'word' | 'html';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

// ─── Helpers ────────────────────────────────────────────────────────────────

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Delay revoke so the browser has time to start the download
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function getAuthHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'x-api-key': 'scopingai',
  };
}

// ─── Download Service ────────────────────────────────────────────────────────

export const downloadService = {
  /**
   * Downloads a PDF by POSTing the rendered HTML to our server, which proxies
   * to the configured external PDF rendering service (server-to-server, no CORS).
   * The server streams back a binary PDF, which we save via a blob URL.
   */
  downloadAsPDF: async (editorState: EditorState, filename = 'template.pdf'): Promise<void> => {
    const html = await ExportService.exportAsHTML(editorState);

    const response = await fetch(`${API_BASE}/api/revisr/export/pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html, options: { format: 'A4' } }),
    });

    if (!response.ok) {
      let errorMessage = `PDF generation failed (${response.status})`;
      try {
        const errorBody = await response.json();
        if (errorBody?.error) errorMessage = errorBody.error;
      } catch {
        // Response wasn't JSON — keep the default status-based message
      }
      throw new Error(errorMessage);
    }

    const blob = await response.blob();
    triggerBlobDownload(blob, filename);
  },

  /**
   * Downloads a standalone HTML file — opens exactly like the preview in any browser.
   * All images are embedded as base64 so the file is completely self-contained.
   */
  downloadAsHTML: async (editorState: EditorState, filename = 'template.html'): Promise<void> => {
    const html = await ExportService.exportAsHTML(editorState);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    triggerBlobDownload(blob, filename);
  },

  /**
   * Downloads a Word (.docx) document.
   * - Text, image, table blocks are natively represented
   * - Complex blocks (shapes, charts) are rendered as images via an off-screen canvas
   */
  downloadAsWord: async (editorState: EditorState, filename = 'template.docx'): Promise<void> => {
    // Lazy-import to avoid loading docx package at startup
    const { generateDocx } = await import('./docxExportService');
    // generateDocx returns a Blob (via Packer.toBlob — browser-native)
    const blob = await generateDocx(editorState);
    triggerBlobDownload(blob, filename);
  },
};
